import React, { useState, useEffect } from 'react';
import { Box, Typography, Paper, alpha, Skeleton, Chip, Tooltip } from '@mui/material';
import { Assessment } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { portfolioAPI } from '../../services/api';

const StatCard = ({ label, value, sub, color, tooltip }) => {
  const content = (
    <Box sx={{
      flex: 1, minWidth: 130,
      display: 'flex', flexDirection: 'column',
      p: 1.2,
      bgcolor: alpha(color, 0.05),
      borderRadius: 1,
      border: `1px solid ${alpha(color, 0.12)}`,
    }}>
      <Typography sx={{ fontSize: '0.58rem', color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.3, mb: 0.3 }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: '1.05rem', fontWeight: 900, color, lineHeight: 1, fontFamily: 'monospace' }}>
        {value}
      </Typography>
      {sub && <Typography sx={{ fontSize: '0.55rem', color: '#94a3b8', mt: 0.2 }}>{sub}</Typography>}
    </Box>
  );
  return tooltip ? <Tooltip title={tooltip} arrow>{content}</Tooltip> : content;
};

const BacktestStats = ({ regime }) => {
  const { t } = useTranslation();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetch = async () => {
      setLoading(true);
      try {
        const res = await portfolioAPI.getRegimeBacktestStats(regime || null);
        setStats(res.data);
      } catch (err) {
        console.error('Failed to load backtest stats:', err);
        setError(t('regimeWatchlist.backtest.loadError', 'Failed to load backtest stats'));
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [regime, t]);

  if (loading) {
    return (
      <Paper elevation={0} sx={{ borderRadius: 0, overflow: 'hidden' }}>
        <Box sx={{ px: 2, py: 1.5, background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)' }}>
          <Skeleton variant="text" width={200} height={24} sx={{ bgcolor: 'rgba(255,255,255,0.1)' }} />
        </Box>
        <Box sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', gap: 1.5, mb: 2 }}>
            {[1,2,3,4].map(i => <Skeleton key={i} variant="rounded" height={70} sx={{ flex: 1, borderRadius: 1 }} />)}
          </Box>
          <Skeleton variant="rounded" height={80} sx={{ borderRadius: 1 }} />
        </Box>
      </Paper>
    );
  }

  if (error || !stats) {
    return (
      <Paper elevation={0} sx={{ borderRadius: 0, p: 2, textAlign: 'center' }}>
        <Typography sx={{ fontSize: '0.75rem', color: '#94a3b8' }}>{error || t('regimeWatchlist.backtest.noData', 'No data')}</Typography>
      </Paper>
    );
  }

  const h = stats.horizons || [];
  const h10 = h.find(x => x.label === 'T+10') || {};
  const regimes = stats.regime_breakdown || [];
  const types = stats.trade_type_breakdown || [];
  const sectors = stats.sector_breakdown || [];
  const isFiltered = stats.regime_filter !== 'ALL';

  return (
    <Paper elevation={0} sx={{ borderRadius: 0, overflow: 'hidden' }}>
      {/* Header */}
      <Box sx={{
        px: 2, py: 1.2,
        background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)',
        color: 'white',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Assessment sx={{ fontSize: 18 }} />
          <Typography sx={{ fontSize: '0.82rem', fontWeight: 800 }}>Backtest Performance</Typography>
          {isFiltered && (
            <Chip label={stats.regime_filter} size="small" sx={{
              height: 20, fontSize: '0.55rem', fontWeight: 800,
              bgcolor: 'rgba(255,255,255,0.15)', color: 'white',
            }} />
          )}
        </Box>
        <Typography sx={{ fontSize: '0.58rem', opacity: 0.7 }}>
          {stats.period} • {stats.total_trades} trades • {stats.params}
        </Typography>
      </Box>

      <Box sx={{ p: 2 }}>
        {/* KPIs */}
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1.5 }}>
          <StatCard label="Avg Return T+10" value={`+${h10.avg_return || 0}%`} sub={`WR ${h10.win_rate || 0}%`} color="#10b981" tooltip={t('regimeWatchlist.backtest.avgReturnTooltip', 'Average return after 10 trading sessions')} />
          <StatCard label="Win Rate T+10" value={`${h10.win_rate || 0}%`} sub={`${stats.total_trades} trades`} color="#3b82f6" tooltip={t('regimeWatchlist.backtest.winRateTooltip', 'Win rate (profit > 0%) after 10 sessions')} />
          <StatCard label="Target Hit" value={`${stats.target_hit_pct || 0}%`} sub={`Stop: ${stats.stop_hit_pct || 0}%`} color="#8b5cf6" tooltip={t('regimeWatchlist.backtest.targetHitTooltip', '% of trades hitting target vs stop loss')} />
          <StatCard label="Max Gain / DD" value={`+${stats.avg_max_gain_pct || 0}%`} sub={`DD: ${stats.avg_max_drawdown_pct || 0}%`} color="#f59e0b" tooltip={t('regimeWatchlist.backtest.maxGainTooltip', 'Avg max gain / avg max drawdown per trade')} />
        </Box>

        {/* Forward Returns Grid */}
        <Box sx={{ display: 'grid', gridTemplateColumns: `repeat(${Math.min(h.length, 6)}, 1fr)`, gap: 0.8, mb: 1.5 }}>
          {h.map((hz, i) => {
            const clr = hz.avg_return > 2 ? '#10b981' : hz.avg_return > 0 ? '#3b82f6' : '#ef4444';
            return (
              <Box key={i} sx={{
                textAlign: 'center', py: 0.8, px: 0.5,
                bgcolor: alpha(clr, 0.05), borderRadius: 1,
                border: `1px solid ${alpha(clr, 0.10)}`,
              }}>
                <Typography sx={{ fontSize: '0.58rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase' }}>
                  {hz.label}
                </Typography>
                <Typography sx={{ fontSize: '0.95rem', fontWeight: 900, color: clr, fontFamily: 'monospace' }}>
                  {hz.avg_return > 0 ? '+' : ''}{hz.avg_return}%
                </Typography>
                <Typography sx={{ fontSize: '0.55rem', color: '#64748b' }}>WR {hz.win_rate}%</Typography>
              </Box>
            );
          })}
        </Box>

        {/* 3-column breakdown: Regime + Trade Type + Sector */}
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          {/* By Regime */}
          {!isFiltered && regimes.length > 0 && (
            <Box sx={{ flex: 1, minWidth: 170 }}>
              <Typography sx={{ fontSize: '0.58rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', mb: 0.4 }}>
                Theo Regime
              </Typography>
              {regimes.map((r, i) => {
                const clr = r.avg_ret10d > 3 ? '#10b981' : r.avg_ret10d > 0 ? '#3b82f6' : '#ef4444';
                return (
                  <Box key={i} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 0.25 }}>
                    <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, color: '#334155' }}>{r.regime}</Typography>
                    <Box sx={{ display: 'flex', gap: 0.8 }}>
                      <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, color: clr, fontFamily: 'monospace' }}>
                        {r.avg_ret10d > 0 ? '+' : ''}{r.avg_ret10d}%
                      </Typography>
                      <Typography sx={{ fontSize: '0.68rem', color: '#64748b', fontFamily: 'monospace', minWidth: 45 }}>
                        WR {r.win_rate}%
                      </Typography>
                    </Box>
                  </Box>
                );
              })}
            </Box>
          )}

          {/* By Trade Type */}
          {types.length > 0 && (
            <Box sx={{ flex: 1, minWidth: 170 }}>
              <Typography sx={{ fontSize: '0.58rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', mb: 0.4 }}>
                {t('regimeWatchlist.backtest.byTradeType', 'By Trade Type')}
              </Typography>
              {types.map((t, i) => (
                <Box key={i} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 0.25 }}>
                  <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, color: '#334155' }}>{t.trade_type}</Typography>
                  <Box sx={{ display: 'flex', gap: 0.8 }}>
                    <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, color: '#10b981', fontFamily: 'monospace' }}>
                      ret10d {t.avg_ret10d > 0 ? '+' : ''}{t.avg_ret10d}%
                    </Typography>
                    <Typography sx={{ fontSize: '0.68rem', color: '#64748b', fontFamily: 'monospace', minWidth: 45 }}>
                      WR {t.win_rate_10d}%
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Box>
          )}

          {/* By Sector (top 5) */}
          {sectors.length > 0 && (
            <Box sx={{ flex: 1, minWidth: 170 }}>
              <Typography sx={{ fontSize: '0.58rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', mb: 0.4 }}>
                {t('regimeWatchlist.backtest.topSectors', 'Top Sectors (ret10d)')}
              </Typography>
              {sectors.slice(0, 5).map((s, i) => {
                const clr = s.avg_ret10d > 2 ? '#10b981' : s.avg_ret10d > 0 ? '#3b82f6' : '#ef4444';
                return (
                  <Box key={i} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 0.25 }}>
                    <Typography sx={{ fontSize: '0.62rem', fontWeight: 600, color: '#475569', maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {s.sector}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 0.8 }}>
                      <Typography sx={{ fontSize: '0.62rem', fontWeight: 700, color: clr, fontFamily: 'monospace' }}>
                        {s.avg_ret10d > 0 ? '+' : ''}{s.avg_ret10d}%
                      </Typography>
                      <Typography sx={{ fontSize: '0.62rem', color: '#94a3b8', fontFamily: 'monospace' }}>
                        ({s.count})
                      </Typography>
                    </Box>
                  </Box>
                );
              })}
            </Box>
          )}
        </Box>
      </Box>
    </Paper>
  );
};

export default BacktestStats;
